import { Link, useNavigate } from "react-router-dom";
import { IMG } from "@/data/demo";
import * as Icons from "lucide-react";

function Icon({ name, ...p }) { const C = Icons[name] || Icons.Circle; return <C {...p} />; }

const PROBLEMS = [
  { p: "You're juggling 20 tools and a dozen logins.", s: "One workspace for clients, content, compliance and growth.", icon: "LayoutGrid" },
  { p: "You forget who needs a follow-up.", s: "MFDx surfaces the exact clients and prospects to contact today.", icon: "PhoneCall" },
  { p: "Creating content eats your evenings.", s: "One-click WhatsApp, posts and reels — personalised for your clients.", icon: "Wand2" },
  { p: "You react to markets & SEBI instead of leading.", s: "Daily briefs decode what matters for your business in 60 seconds.", icon: "Radar" },
];
const WORKFLOWS = [
  { h: "Your daily command centre", d: "Open MFDx and instantly see what needs attention, what to do, and how your business is growing — a Growth Score that climbs as you act.", icon: "LayoutDashboard", c: "#FF8A00", img: IMG.skyline[0] },
  { h: "An AI Copilot that works ahead of you", d: "\"3 clients need a check-in.\" \"Draft a WhatsApp for SIP investors.\" Your Copilot spots opportunities and drafts the work.", icon: "Sparkles", c: "#A855F7", img: IMG.advisor[0] },
  { h: "Client 360 that tells you what to do next", d: "Every client's holdings, engagement and attrition risk — with a clear next action and a one-click WhatsApp message.", icon: "UserSquare2", c: "#10B981", img: IMG.advisor[2] },
  { h: "Grow with content, community & learning", d: "Marketing Factory, a 100,000-strong MFD community, and 7-minute skills — all built to grow your AUM.", icon: "TrendingUp", c: "#3B82F6", img: IMG.marketing[0] },
];
const STATS = [["1,00,000+", "MFDs in the community"], ["Daily", "live discussions & polls"], ["Pay-as-you-use", "no subscriptions"]];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#090A0D] text-slate-200" style={{ fontFamily: "'Inter',sans-serif" }}>
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#0E1117]/85 backdrop-blur-xl border-b border-[#222938]">
        <div className="max-w-6xl mx-auto flex items-center gap-3 px-5 h-16">
          <div className="w-9 h-9 rounded-xl grad-amber grid place-items-center text-black font-head font-bold amber-glow">M</div>
          <span className="font-head font-bold text-xl tracking-tight text-slate-50">MFDX</span>
          <nav className="hidden md:flex items-center gap-6 ml-8 text-sm text-slate-400">
            <a href="#how" className="hover:text-slate-100">How it works</a>
            <a href="#why" className="hover:text-slate-100">Why MFDx</a>
            <a href="#pricing" className="hover:text-slate-100">Pay as you use</a>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white">Log in</Link>
            <Link to="/signup" data-testid="nav-signup-btn" className="px-4 py-2 text-sm font-semibold text-black rounded-full grad-amber amber-glow">Start Free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/12 blur-3xl" />
        <div className="absolute top-40 -left-24 w-80 h-80 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-center relative">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1.5 text-xs font-semibold mb-5">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
              India's largest community of Mutual Fund Distributors
            </div>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-50">
              Grow together with<br />the MFD <span className="text-amber-400">community.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-400 max-w-xl">
              Join thousands of mutual fund distributors sharing wins, answering questions and learning daily. Business tools come built-in — but the community comes first.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-start-btn" className="px-6 py-3.5 rounded-full grad-amber text-black font-semibold amber-glow hover:brightness-105 transition flex items-center gap-2">
                Start Free <Icon name="ArrowRight" size={18} />
              </Link>
              <a href="#how" data-testid="hero-explore-btn" className="px-6 py-3.5 rounded-full bg-[#181D2A] border border-[#2E384D] font-semibold text-slate-200 hover:border-amber-500/40 transition">See how it works</a>
            </div>
            <p className="mt-5 text-sm text-slate-500 flex items-center gap-2">
              <Icon name="CheckCircle2" size={16} className="text-emerald-400" /> No subscriptions. Pay only for what you use.
            </p>
          </div>

          {/* Hero dashboard mock */}
          <div className="relative fade-up" style={{ animationDelay: ".15s" }}>
            <div className="rounded-3xl bg-[#12151F] border border-[#222938] p-5 shadow-[0_24px_60px_rgba(0,0,0,.5)]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">Your MFDx today</span>
                <span className="flex items-center gap-1.5 bg-emerald-500/12 text-emerald-400 rounded-full px-2.5 py-1 text-xs font-bold">Growth Score 73</span>
              </div>
              <div className="rounded-2xl bg-[#0E1117] border border-[#222938] p-4 mb-3">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">What should I do today?</div>
                <div className="font-head font-bold text-slate-100 text-lg mt-1">Good morning, Arjun</div>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  {[["3", "follow-ups"], ["2", "prospects"], ["1", "content idea"]].map(([n, l]) => (
                    <span key={l} className="flex items-center gap-1 bg-[#181D2A] border border-[#2E384D] rounded-full px-2.5 py-1"><b className="text-amber-400">{n}</b><span className="text-slate-400">{l}</span></span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl bg-gradient-to-br from-amber-500/12 to-transparent border border-amber-500/20 p-4">
                <div className="flex items-center gap-2 text-xs text-amber-400 mb-1"><Icon name="Sparkles" size={14} /> MFDx Copilot</div>
                <p className="text-sm text-slate-200">Markets dipped today — 3 clients may feel anxious. Want me to draft a calming WhatsApp message?</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-3 flex items-center gap-2"><Icon name="AlertTriangle" size={16} className="text-red-400" /><span className="text-xs font-medium text-red-300">23 SIPs at risk</span></div>
                <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/20 p-3 flex items-center gap-2"><Icon name="TrendingUp" size={16} className="text-emerald-400" /><span className="text-xs font-medium text-emerald-300">AUM +6.4%</span></div>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-5 bg-[#181D2A] border border-[#2E384D] rounded-2xl px-4 py-3 animate-floaty hidden sm:flex items-center gap-2">
              <Icon name="Radar" size={18} className="text-amber-400" /><span className="text-sm font-semibold text-slate-100">Retention Radar</span>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof stats */}
      <section className="max-w-6xl mx-auto px-5 pb-4">
        <div className="grid grid-cols-3 gap-3">
          {STATS.map(([n, l]) => (
            <div key={l} className="rounded-2xl bg-[#12151F] border border-[#222938] p-5 text-center">
              <div className="font-head font-extrabold text-2xl md:text-3xl text-slate-50">{n}</div>
              <div className="text-xs text-slate-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Teaser video */}
      <section className="max-w-6xl mx-auto px-5 pt-10 pb-4" data-testid="teaser-section">
        <div className="rounded-[28px] bg-[#0E1117] border border-[#222938] p-6 md:p-10 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full px-3 py-1.5 text-xs font-semibold mb-4">
              <Icon name="PlayCircle" size={14} /> Watch the 15-second teaser
            </div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-slate-50">Give us 5 minutes. We'll help you upgrade your business.</h2>
            <p className="text-slate-400 mt-3 text-lg max-w-xl">Sharpen your skills, grow your practice and join thousands of MFDs already levelling up on MFDx.</p>
            <Link to="/signup" data-testid="teaser-cta-btn" className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full grad-amber text-black font-semibold amber-glow hover:brightness-105 transition">
              Register now <Icon name="ArrowRight" size={18} />
            </Link>
          </div>
          <div className="w-full max-w-[300px] shrink-0">
            <div className="rounded-3xl overflow-hidden border border-[#2A3242] shadow-[0_24px_60px_rgba(0,0,0,.5)] bg-black aspect-[9/16]">
              <video data-testid="teaser-video" src="/mfdx-teaser.mp4" autoPlay muted loop playsInline controls
                className="w-full h-full object-cover" aria-label="MFDx teaser video" />
            </div>
          </div>
        </div>
      </section>

      {/* Community first */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="rounded-[28px] bg-gradient-to-br from-[#0F1A14] to-[#0C0E14] border border-emerald-500/20 p-8 md:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-emerald-500/10 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full px-3 py-1.5 text-xs font-semibold mb-4">
              <Icon name="Users" size={14} /> Community first
            </div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-slate-50 max-w-2xl">You're not building your practice alone anymore.</h2>
            <p className="text-slate-400 mt-3 max-w-2xl text-lg">Ask a question at midnight and wake up to answers. Share a win. Vote in polls. See what's working for MFDs just like you — then use the built-in tools to act on it.</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[["MessageSquare", "Ask & answer", "Real questions, real answers from working MFDs — never marketing fluff."], ["BarChart3", "Polls & pulse", "See how peers are pricing, prospecting and positioning right now."], ["Trophy", "Recognition", "Ranked by contribution and learning — never by AUM or returns."]].map(([ic, h, d]) => (
                <div key={h} className="rounded-2xl bg-[#0E1117] border border-[#222938] p-5">
                  <span className="w-10 h-10 rounded-xl bg-emerald-500/12 grid place-items-center mb-3"><Icon name={ic} size={20} className="text-emerald-400" /></span>
                  <div className="font-head font-semibold text-slate-100">{h}</div>
                  <p className="text-sm text-slate-400 mt-1">{d}</p>
                </div>
              ))}
            </div>
            <Link to="/signup" data-testid="community-cta-btn" className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full grad-amber text-black font-semibold amber-glow hover:brightness-105 transition">
              Join the community free <Icon name="ArrowRight" size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Problems → solutions */}
      <section id="why" className="max-w-6xl mx-auto px-5 py-14">
        <h2 className="font-head text-3xl md:text-4xl font-bold text-slate-50 text-center">Everything that runs your MFD practice, in one place.</h2>
        <p className="text-center text-slate-500 mt-2">Every daily task turned into one clear next action.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-10">
          {PROBLEMS.map((x, i) => (
            <div key={x.p} className="rounded-2xl bg-[#12151F] border border-[#222938] p-6 fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <span className="w-11 h-11 rounded-xl bg-amber-500/12 grid place-items-center mb-4"><Icon name={x.icon} size={22} className="text-amber-400" /></span>
              <div className="text-slate-500 text-sm line-through decoration-red-400/50">{x.p}</div>
              <div className="font-head font-semibold text-slate-100 mt-1 flex items-start gap-2"><Icon name="ArrowRight" size={16} className="text-amber-400 mt-1 shrink-0" />{x.s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Core workflows */}
      <section id="how" className="max-w-6xl mx-auto px-5 py-6 space-y-6">
        {WORKFLOWS.map((s, i) => (
          <div key={s.h} className={`rounded-3xl bg-[#12151F] border border-[#222938] p-8 md:p-10 flex flex-col md:flex-row items-center gap-8 ${i % 2 ? "md:flex-row-reverse" : ""}`}>
            <div className="flex-1">
              <div className="w-12 h-12 rounded-2xl grid place-items-center mb-4" style={{ background: `${s.c}22` }}><Icon name={s.icon} size={24} style={{ color: s.c }} /></div>
              <h3 className="font-head text-2xl md:text-3xl font-bold text-slate-50">{s.h}</h3>
              <p className="text-slate-400 mt-2 text-lg">{s.d}</p>
            </div>
            <div className="flex-1 w-full">
              <div className="rounded-2xl h-44 md:h-56 relative overflow-hidden border border-[#222938]">
                <img src={s.img} alt={s.h} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${s.c}66, #090A0Daa 75%)` }} />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Pay as you use */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-12">
        <div className="rounded-[28px] bg-[#12151F] border border-[#222938] p-8 md:p-12 flex flex-col md:flex-row items-center gap-6">
          <div className="flex-1">
            <h2 className="font-head text-3xl md:text-4xl font-bold text-slate-50">Stop paying for 20 different tools.</h2>
            <p className="mt-2 text-slate-400 text-lg">One prepaid wallet powers everything. Use what you need, pay only for what you use.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[["AI Video", "₹35/min"], ["AI Calling", "₹5/min"], ["Creative", "₹10/gen"], ["AI Email", "₹1/email"]].map(([a, b]) => (
              <div key={a} className="bg-[#0E1117] border border-[#222938] rounded-2xl px-4 py-3"><div className="font-bold text-amber-400">{b}</div><div className="text-slate-500 text-xs">{a}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-5 py-12">
        <div className="rounded-[32px] bg-gradient-to-br from-[#1A130A] to-[#0C0E14] border border-amber-500/20 p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/15 blur-3xl" />
          <h2 className="font-head text-3xl md:text-5xl font-bold text-slate-50 relative">Your business. Your clients.<br />Your AI Copilot.</h2>
          <p className="text-slate-400 mt-4 relative">Spend less time managing your business. Spend more time growing it.</p>
          <button onClick={() => nav("/signup")} data-testid="final-cta-btn" className="mt-8 px-8 py-4 rounded-full grad-amber text-black font-semibold amber-glow hover:brightness-105 transition relative inline-flex items-center gap-2">
            Start Free <Icon name="ArrowRight" size={18} />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#222938] py-8 text-center text-sm text-slate-600">
        MFDX · The AI operating system for MFDs · Demo prototype — data shown is illustrative, not investment advice.
      </footer>
    </div>
  );
}
